import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

// COMPONENTS
import SideMenu from 'components/SideMenu';
import FleetSettingsRoutes from 'routes/fleetSettingsRoutes';
import { Spinner } from 'components/Loading';

// HOOKS
import useStrategy, { FeatureFlags } from 'hooks/useStrategy';
import usePlans from 'hooks/usePlans';
import { useHierarchy } from 'hooks';

// SELECTORS
import { selectDealership, selectLoading } from 'services/Authentication/selectors';

// CONSTANTS
import { FLEET_SETTINGS_GEOFENCING_SIDE_MENU_ITEM, FLEET_SETTINGS_SIDE_MENU_ITEMS } from 'constants/common';
import { FLEET_PLANS } from 'constants/plans';

// STYLES
import {
  Title,
  SettingsPageView,
  HeaderContainer,
  DealershipNameContainer,
  TitleContainer,
} from 'views/FleetSettings/FleetSettings.styles';
import { SettingsPageContainer } from 'styles/settings';

const FleetSettings = () => {
  const intl = useIntl();
  const { isDealershipPlanEqualOrAbove } = usePlans();
  const { currentlySelectedEntity, hierarchy, hasChildren } = useHierarchy();
  const ownDealership = useSelector(selectDealership());
  const loading = useSelector(selectLoading());

  const selectedName =
    typeof currentlySelectedEntity === 'number'
      ? hierarchy.find((h) => h.value === currentlySelectedEntity)?.label
      : ownDealership?.name;

  const items = useMemo(() => {
    const items = [...FLEET_SETTINGS_SIDE_MENU_ITEMS];

    if (isDealershipPlanEqualOrAbove(FLEET_PLANS.pro)) {
      items.push(FLEET_SETTINGS_GEOFENCING_SIDE_MENU_ITEM);
    }

    return items;
  }, [isDealershipPlanEqualOrAbove]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <SettingsPageContainer>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: 'views.Settings.helmetTitle',
          })}
        </title>
        <meta
          name="settings"
          content={intl.formatMessage({
            id: 'views.Settings.helmetDescription',
          })}
        />
      </Helmet>

      <HeaderContainer>
        <TitleContainer>
          <Title>{intl.formatMessage({ id: 'components.SideMenu.fleetSettingsTitle' })}</Title>
        </TitleContainer>
        <DealershipNameContainer>{hasChildren && selectedName}</DealershipNameContainer>
      </HeaderContainer>

      <SettingsPageView>
        <SideMenu items={items} />
        <FleetSettingsRoutes />
      </SettingsPageView>
    </SettingsPageContainer>
  );
};

export default FleetSettings;
